import * as React from 'react';
import { MoreHorizontal, ExternalLink, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ooh/ui/components/dropdown-menu';
import { TableCell, TableRow } from '@ooh/ui/components/table';
import { cn } from '@ooh/ui/lib/utils';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import { Link } from '../../plugs/link';
import { ExpiryDeadlineCell } from './expiry-deadline-cell';
import { ReservationQueueList } from './reservation-queue-list';
import type { QueueAction, QueueEntryView } from './reservation-queue-list';
import { ReservationStatusBadge } from './reservation-status-badge';
import { buildRowActions, STICKY_COL_SHADOW } from './unit-reservations-tab-parts';
import type {
  UnitQueueLabels,
  UnitReservationRow,
  UnitReservationsPermissions,
  UnitReservationsTabLabels,
} from './unit-reservations-tab-types';

export interface UnitReservationTableRowProps {
  item: UnitReservationRow;
  index: number;
  permissions: UnitReservationsPermissions;
  labels: UnitReservationsTabLabels;
  currentUserId: number;
  isFocused: boolean;
  isHighlighted: boolean;
  focusRowRef: React.RefObject<HTMLTableRowElement | null>;
  queueEntries: QueueEntryView[];
  queueLabels?: UnitQueueLabels;
  isQueueOpen: boolean;
  onToggleQueue: (reservationId: number) => void;
  onAction: (action: string, reservationId: number) => void;
  onQueueAction?: (action: QueueAction, entryId: number, reservationId: number) => void;
  onOpenFullQueue?: (reservationId: number) => void;
  openFullQueueLabel?: string;
}

// eslint-disable-next-line complexity -- row renderer with optional queue affordance; extraction would split a cohesive row.
export function UnitReservationTableRow({
  item,
  index: idx,
  permissions,
  labels,
  currentUserId,
  isFocused,
  isHighlighted,
  focusRowRef,
  queueEntries,
  queueLabels,
  isQueueOpen,
  onToggleQueue,
  onAction,
  onQueueAction,
  onOpenFullQueue,
  openFullQueueLabel,
}: UnitReservationTableRowProps) {
  const rowActions = buildRowActions(item, permissions, currentUserId, labels);
  const isQueued = item.isQueued;
  const hasQueue = queueEntries.length > 0 && queueLabels !== undefined;
  return (
    <>
      <TableRow
        ref={isFocused ? focusRowRef : null}
        data-testid={`unit-reservation-row-${item.id}`}
        className={cn(
          'border-b border-border/60 transition-colors hover:bg-muted/30',
          idx % 2 === 0 ? 'bg-background' : 'bg-muted/10',
          isFocused && isHighlighted && 'ring-2 ring-primary ring-inset',
        )}
      >
        {/* Status — red text label only for queued HARD */}
        <TableCell className="px-3 py-2.5">
          <ReservationStatusBadge
            status={item.status}
            isQueued={isQueued}
            statusLabel={item.statusLabel}
          />
        </TableCell>
        <TableCell className="px-3 py-2.5">
          <span className="text-[13px]">{item.holderUserName}</span>
        </TableCell>
        <TableCell className="px-3 py-2.5">
          <span className="text-[13px]">{formatDisplayDate(item.dateFrom)}</span>
        </TableCell>
        <TableCell className="px-3 py-2.5">
          <span className="text-[13px]">{formatDisplayDate(item.dateTo)}</span>
        </TableCell>
        {/* Expiry deadline — see header comment above. */}
        <TableCell className="px-3 py-2.5">
          <ExpiryDeadlineCell
            status={item.status}
            effectiveExpiresAt={item.effectiveExpiresAt ?? null}
            labels={{
              neverExpiresLabel: labels.deadlineNeverExpiresLabel ?? '',
              noDeadlineSetLabel: labels.deadlineNoDeadlineSetLabel ?? '',
              expiredMarkerLabel: labels.deadlineExpiredMarkerLabel ?? '',
            }}
            testId={`unit-reservation-deadline-${String(item.id)}`}
          />
        </TableCell>
        {/* Offer — clickable when offer exists */}
        <TableCell className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          {item.offerName && item.offerHref ? (
            <a
              href={item.offerHref}
              className="flex items-center gap-1 text-[13px] text-primary hover:text-primary/80 max-w-[160px] group"
            >
              <span className="truncate">{item.offerName}</span>
              <ExternalLink className="h-3 w-3 shrink-0 opacity-60 group-hover:opacity-100" />
            </a>
          ) : (
            <span className="text-[13px] text-muted-foreground">{labels.noOfferLabel}</span>
          )}
        </TableCell>
        <TableCell className="px-3 py-2.5">
          <span className="text-2xs text-muted-foreground">{item.sourceLabel}</span>
        </TableCell>
        <TableCell className="px-3 py-2.5 text-center">
          {hasQueue && queueLabels ? (
            <Button
              variant="outline"
              size="xs"
              data-testid={`queue-toggle-${item.id}`}
              aria-expanded={isQueueOpen}
              onClick={() => onToggleQueue(item.id)}
              className="gap-1"
            >
              <Users className="h-3 w-3" />
              <span className="tabular-nums">{queueEntries.length}</span>
              {isQueueOpen ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
          ) : null}
        </TableCell>
        {/* Sticky actions — solid bg-background always, no alternation to prevent bleed */}
        <TableCell
          className={cn(
            'sticky right-0 z-10 px-2 py-2.5 text-center bg-background',
            STICKY_COL_SHADOW,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {rowActions.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid={`reservation-row-actions-trigger-${item.id}`}
                  className="flex items-center justify-center h-7 w-7 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors mx-auto"
                  aria-label={labels.actionsLabel}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {rowActions.map((a) => (
                  <React.Fragment key={a.action}>
                    {a.separator ? <DropdownMenuSeparator /> : null}
                    {a.href ? (
                      <DropdownMenuItem
                        asChild
                        className={a.destructive ? 'text-destructive focus:text-destructive' : ''}
                      >
                        <Link to={a.href} data-testid={`reservation-action-${a.action}-${item.id}`}>
                          {a.label}
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        data-testid={`reservation-action-${a.action}-${item.id}`}
                        className={a.destructive ? 'text-destructive focus:text-destructive' : ''}
                        onClick={() => onAction(a.action, item.id)}
                      >
                        {a.label}
                      </DropdownMenuItem>
                    )}
                  </React.Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </TableCell>
      </TableRow>
      {hasQueue && isQueueOpen && queueLabels && onQueueAction ? (
        <TableRow
          data-testid={`unit-reservation-queue-row-${item.id}`}
          className="border-b border-border/60 bg-muted/10 hover:bg-muted/10"
        >
          <TableCell colSpan={9} className="px-3 py-2">
            {onOpenFullQueue && openFullQueueLabel ? (
              <div className="mb-1.5 flex justify-end">
                <button
                  type="button"
                  data-testid={`unit-reservation-open-full-queue-${item.id}`}
                  onClick={() => onOpenFullQueue(item.id)}
                  className="text-2xs text-primary hover:text-primary/80 hover:underline"
                >
                  {openFullQueueLabel}
                </button>
              </div>
            ) : null}
            <ReservationQueueList
              reservationId={item.id}
              entries={queueEntries}
              currentUserId={currentUserId}
              permissions={{
                canManageQueue: permissions.canManageWaitlist,
                // CU-86cb3x6y6: displace-and-promote requires BOTH
                // manage_waitlist AND force_release — see
                // ReservationQueueListPermissions.canDisplacePromote.
                // The production promote mutation itself still needs
                // its own fix + confirmation-dialog wiring (tracked
                // separately for the full implementation).
                canDisplacePromote: permissions.canManageWaitlist && permissions.canForceRelease,
              }}
              labels={queueLabels}
              onQueueAction={(action, entryId) => onQueueAction(action, entryId, item.id)}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
